import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  Box,
  Typography,
  Paper,
  Grid,
  Avatar,
  Chip,
  Alert,
  Tooltip,
  useTheme,
} from '@mui/material';
import {
  Person as PersonIcon,
  NavigateNext as NavigateNextIcon,
  Replay as ReplayIcon,
} from '@mui/icons-material';
import DOMPurify from 'dompurify';
import {
  validateRolePlayGameData,
  isValidRolePlayGameData,
  createSampleRolePlayGameData,
} from '../utils/rolePlayGameUtils';
import Button from './UIKit/Button';
import Card from './UIKit/Card';
import TextField from './UIKit/TextField';

// Импортируем изображения маскота
import noSmileImg from '../assets/mascot/no_smile.png';
import smileImg from '../assets/mascot/smile.png';
import smileHatImg from '../assets/mascot/smile_hat.png';
import smileHatGlassImg from '../assets/mascot/smile_hat_glass.png';
import smileHatGlassScarf from '../assets/mascot/smile_hat_glass_scarf.png';

/**
 * Компонент RolePlayGame - ролевая игра для интерактивного изучения материала
 * @param {Object} options - Настройки игры
 * @param {Object} options.character - Информация о персонаже
 * @param {string} options.character.name - Имя персонажа
 * @param {string} options.character.avatar - URL аватара персонажа
 * @param {string} options.character.role - Роль персонажа
 * @param {string} options.character.description - Описание персонажа
 * @param {Array} options.scenario - Сценарий диалогов и выборов
 * @param {function} onChange - Функция обратного вызова при изменении прогресса или выборов
 */
const RolePlayGame = ({ options, onChange }) => {
  const theme = useTheme();
  // Создаем стабильный ID для компонента
  const componentId = useMemo(
    () => Math.random().toString(36).substring(7),
    []
  );

  const [currentSceneIndex, setCurrentSceneIndex] = useState(0);
  const [dialogHistory, setDialogHistory] = useState([]);
  const [userChoices, setUserChoices] = useState([]);
  const [userInput, setUserInput] = useState('');
  const [error, setError] = useState(null);
  const [gameCompleted, setGameCompleted] = useState(false);
  const [choiceHistory, setChoiceHistory] = useState({});
  const [mascotImage, setMascotImage] = useState(noSmileImg);

  // Обработка полученных опций с использованием утилит валидации
  const processedOptions = useMemo(() => {
    try {
      // Используем утилиту валидации для проверки данных
      const validData = validateRolePlayGameData(
        options,
        'RolePlayGame компонент'
      );

      // Проверяем, валидны ли данные для игры
      if (!isValidRolePlayGameData(validData)) {
        console.warn(
          'RolePlayGame: Данные не содержат корректного сценария',
          validData
        );
        setError('Данные не содержат корректного сценария');

        // Если нет данных и это режим разработки, создаем тестовые данные
        if (process.env.NODE_ENV === 'development') {
          console.info('RolePlayGame: Создаем тестовые данные для разработки');
          return createSampleRolePlayGameData();
        }
        return null;
      }

      setError(null);
      return validData;
    } catch (err) {
      console.error('RolePlayGame: Ошибка при обработке опций', err);
      setError('Ошибка при обработке опций: ' + err.message);
      return null;
    }
  }, [options]);

  // Инициализация игры при изменении опций
  useEffect(() => {
    if (processedOptions) {
      // Добавляем первую сцену в историю диалогов
      if (processedOptions.scenario && processedOptions.scenario.length > 0) {
        setCurrentSceneIndex(0);
        setDialogHistory([]);
        setUserChoices([]);
        setGameCompleted(false);
        // Не сбрасываем историю выборов при повторной инициализации
        // setChoiceHistory({});
      }
    }
  }, [processedOptions]);

  // Обновляем родительский компонент при изменении выборов пользователя
  useEffect(() => {
    if (userChoices.length > 0 && onChange) {
      onChange(JSON.stringify(userChoices));
    }
  }, [userChoices, onChange]);

  // Обработка текущей сцены при изменении индекса
  useEffect(() => {
    if (
      processedOptions &&
      processedOptions.scenario &&
      currentSceneIndex < processedOptions.scenario.length
    ) {
      const currentScene = processedOptions.scenario[currentSceneIndex];

      // Автоматически добавляем диалоги в историю
      if (currentScene.type === 'dialog') {
        setDialogHistory((prev) => [
          ...prev,
          {
            type: 'character',
            text: currentScene.text,
          },
        ]);
      }

      // Проверяем завершение игры
      if (currentScene.type === 'end') {
        setGameCompleted(true);
        setDialogHistory((prev) => [
          ...prev,
          {
            type: 'system',
            text: currentScene.text,
          },
        ]);
      }
    }
  }, [currentSceneIndex, processedOptions]);

    // Добавим информативный счетчик прогресса вариантов
  const getTotalChoicesStats = useCallback(() => {
    if (!processedOptions || !processedOptions.scenario)
      return { chosen: 0, total: 0 };

    // Подсчитываем общее количество вариантов выбора в игре
    let totalChoices = 0;
    let chosenCount = 0;

    processedOptions.scenario.forEach((scene, index) => {
      if (scene.type === 'choice') {
        totalChoices += scene.choices.length;
        // Подсчитываем количество выбранных вариантов
        const chosen = choiceHistory[index] || [];
        chosenCount += chosen.length;
      }
    });

    return { chosen: chosenCount, total: totalChoices };
  }, [processedOptions, choiceHistory]);

    // Функция для обновления маскота на основе прогресса
  const updateMascot = useCallback(() => {
    const { chosen, total } = getTotalChoicesStats();

    // Рассчитываем процент прогресса
    const progressPercent = total > 0 ? (chosen / total) * 100 : 0;

    // Обновляем изображение кота в зависимости от прогресса
    if (progressPercent >= 100) {
      setMascotImage(smileHatGlassScarf);
    } else if (progressPercent >= 80) {
      setMascotImage(smileHatGlassImg);
    } else if (progressPercent >= 60) {
      setMascotImage(smileHatImg);
    } else if (progressPercent >= 40) {
      setMascotImage(smileImg);
    } else if (progressPercent >= 20) {
      setMascotImage(noSmileImg);
    } else {
      setMascotImage(noSmileImg);
    }
  }, [getTotalChoicesStats]);

  // Обновляем состояние кота при новых выборах
  useEffect(() => {
    updateMascot();
  }, [choiceHistory, updateMascot]);

  // Получаем описание маскота
  const getMascotDescription = () => {
    switch (mascotImage) {
      case smileHatGlassScarf:
        return 'Кот-помощник в шляпе, очках и шарфе. Вы исследовали все возможные варианты!';
      case smileHatGlassImg:
        return 'Кот-помощник в шляпе и очках. Продолжайте исследовать варианты!';
      case smileHatImg:
        return 'Кот-помощник в шляпе. Вы на полпути!';
      case smileImg:
        return 'Улыбающийся кот-помощник. Продолжайте исследовать!';
      case noSmileImg:
        return 'Кот-помощник. Исследуй разные варианты, чтобы улучшить его!';
      default:
        return 'Кот-помощник. Исследуй разные варианты, чтобы улучшить его!';
    }
  };

  // Получаем цвет фона для кота в зависимости от настроения
  const getMascotBgColor = () => {
    switch (mascotImage) {
      case smileHatGlassScarf:
        return '#fff8e1'; // светло-желтый
      case smileHatGlassImg:
      case smileHatImg:
        return '#e3f2fd'; // светло-голубой
      case smileImg:
        return '#e8f5e9'; // светло-зеленый
      default:
        return '#f5f5f5'; // светло-серый
    }
  };

  // Функция перехода к следующей сцене
  const goToNextScene = () => {
    if (
      processedOptions &&
      processedOptions.scenario &&
      currentSceneIndex < processedOptions.scenario.length - 1
    ) {
      setCurrentSceneIndex((prev) => prev + 1);
    }
  };

  // Функция перехода к определенной сцене
  const goToScene = (sceneIndex) => {
    if (
      processedOptions &&
      processedOptions.scenario &&
      sceneIndex < processedOptions.scenario.length
    ) {
      console.log(`Переход к сцене №${sceneIndex}`);
      setCurrentSceneIndex(sceneIndex);
    } else {
      console.warn(
        `Невозможно перейти к сцене №${sceneIndex}: выход за пределы сценария или сценарий не загружен`
      );
      // Если сцена недоступна, попробуем перейти к следующей
      goToNextScene();
    }
  };

  // Обработка выбора пользователя
  const handleChoice = (choice) => {
    console.log('Пользователь выбрал:', choice);

    // Добавляем выбор в историю
    setDialogHistory((prev) => [
      ...prev,
      {
        type: 'user',
        text: choice.text,
      },
    ]);

    // Добавляем выбор в массив выборов пользователя
    setUserChoices((prev) => [...prev, choice]);

    // Запоминаем, что этот вариант уже был выбран
    setChoiceHistory((prev) => ({
      ...prev,
      [currentSceneIndex]: [...(prev[currentSceneIndex] || []), choice.text],
    }));

    // Переходим к следующей сцене
    if (choice.nextScene !== undefined) {
      console.log(`Переход к указанной сцене №${choice.nextScene}`);
      setTimeout(() => {
        goToScene(choice.nextScene);
      }, 1000); // Добавляем небольшую задержку для улучшения пользовательского опыта
    } else {
      console.log('Переход к следующей сцене');
      setTimeout(() => {
        goToNextScene();
      }, 1000);
    }
  };

  // Обработка ввода пользователя
  const handleInputSubmit = () => {
    if (!userInput.trim()) return;

    const currentScene = processedOptions.scenario[currentSceneIndex];
    console.log(
      `Обработка ввода для сцены №${currentSceneIndex}`,
      currentScene
    );

    // Добавляем ввод в историю
    setDialogHistory((prev) => [
      ...prev,
      {
        type: 'user',
        text: userInput,
      },
    ]);

    // Проверяем правильность ответа
    const isCorrect = userInput
      .toLowerCase()
      .includes(currentScene.correctAnswer.toLowerCase());
    console.log(
      `Ответ ${isCorrect ? 'правильный' : 'неправильный'}: ${userInput}`
    );

    // Добавляем обратную связь
    setDialogHistory((prev) => [
      ...prev,
      {
        type: 'feedback',
        text: isCorrect
          ? currentScene.feedback.correct
          : currentScene.feedback.incorrect,
        status: isCorrect ? 'correct' : 'incorrect',
      },
    ]);

    // Добавляем ввод в массив выборов пользователя
    setUserChoices((prev) => [
      ...prev,
      {
        input: userInput,
        isCorrect,
      },
    ]);

    // Если ответ правильный, запоминаем его для этой сцены
    if (isCorrect) {
      setChoiceHistory((prev) => ({
        ...prev,
        [currentSceneIndex]: [...(prev[currentSceneIndex] || []), userInput],
      }));
    }

    // Сбрасываем поле ввода
    setUserInput('');

    // Переходим к следующей сцене
    if (isCorrect && currentScene.nextScene !== undefined) {
      console.log(`Переход к указанной сцене №${currentScene.nextScene}`);
      setTimeout(() => {
        goToScene(currentScene.nextScene);
      }, 1000); // Добавляем небольшую задержку для улучшения пользовательского опыта
    } else if (isCorrect) {
      console.log('Переход к следующей сцене');
      setTimeout(() => {
        goToNextScene();
      }, 1000);
    }
  };

  // Перезапуск игры
  const restartGame = () => {
    setCurrentSceneIndex(0);
    setDialogHistory([]);
    setUserChoices([]);
    setGameCompleted(false);
    // Не очищаем историю выборов при перезапуске игры
    // setChoiceHistory({});
  };

  // Рендеринг текущей сцены
  const renderCurrentScene = () => {
    if (
      !processedOptions ||
      !processedOptions.scenario ||
      currentSceneIndex >= processedOptions.scenario.length
    ) {
      return null;
    }

    const currentScene = processedOptions.scenario[currentSceneIndex];

    switch (currentScene.type) {
      case 'dialog':
        return (
          <Box sx={{ mb: 2, display: 'flex', justifyContent: 'flex-end' }}>
            <Button
              variant="contained"
              color="primary"
              endIcon={<NavigateNextIcon />}
              onClick={goToNextScene}
            >
              Далее
            </Button>
          </Box>
        );

      case 'choice':
        // Получаем список уже выбранных вариантов для текущей сцены
        const chosenOptions = choiceHistory[currentSceneIndex] || [];
        const allOptionsChosen =
          chosenOptions.length === currentScene.choices.length;

        return (
          <Box sx={{ mb: 2 }}>
            <Paper elevation={3} sx={{ p: 2, bgcolor: 'background.paper' }}>
              <Typography variant="h6" sx={{ mb: 2, color: 'text.primary' }}>
                {currentScene.question}
              </Typography>
              {allOptionsChosen && (
                <Alert severity="info" sx={{ mb: 2 }}>
                  Вы уже выбрали все доступные варианты. Можете перейти к
                  следующей части урока.
                </Alert>
              )}
              <Grid container spacing={2}>
                {currentScene.choices.map((choice, index) => {
                  const isChosen = chosenOptions.includes(choice.text);
                  return (
                    <Grid item xs={12} key={index}>
                      <Button
                        fullWidth
                        variant={isChosen ? 'contained' : 'outlined'}
                        color={isChosen ? 'secondary' : 'primary'}
                        onClick={() => handleChoice(choice)}
                        disabled={isChosen}
                        sx={{
                          opacity: isChosen ? 0.7 : 1,
                          position: 'relative',
                          '&::after': isChosen
                            ? {
                                content: '"✓"',
                                position: 'absolute',
                                right: '10px',
                                fontSize: '1.2rem',
                              }
                            : {},
                        }}
                      >
                        {choice.text}
                        {isChosen && <span> (Выбрано)</span>}
                      </Button>
                    </Grid>
                  );
                })}
              </Grid>
              {allOptionsChosen && (
                <Box
                  sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}
                >
                  <Button
                    variant="contained"
                    color="primary"
                    endIcon={<NavigateNextIcon />}
                    onClick={goToNextScene}
                  >
                    Продолжить
                  </Button>
                </Box>
              )}
            </Paper>
          </Box>
        );

      case 'input':
        // Проверяем, есть ли правильный ответ в истории
        const hasCorrectAnswer =
          (choiceHistory[currentSceneIndex] || []).length > 0;

        return (
          <Box sx={{ mt: 2 }}>
            <Paper elevation={3} sx={{ p: 2, bgcolor: 'background.paper' }}>
              <Typography variant="h6" sx={{ mb: 2, color: 'text.primary' }}>
                {currentScene.question}
              </Typography>

              {hasCorrectAnswer ? (
                <Alert severity="success" sx={{ mb: 2 }}>
                  Вы уже правильно ответили на этот вопрос. Ваш ответ:
                  <strong> {choiceHistory[currentSceneIndex][0]}</strong>
                  <Box sx={{ mt: 2 }}>
                    <Button
                      variant="contained"
                      color="primary"
                      endIcon={<NavigateNextIcon />}
                      onClick={() =>
                        currentScene.nextScene !== undefined
                          ? goToScene(currentScene.nextScene)
                          : goToNextScene()
                      }
                    >
                      Продолжить
                    </Button>
                  </Box>
                </Alert>
              ) : (
                <>
                  <TextField
                    fullWidth
                    variant="outlined"
                    value={userInput}
                    onChange={(e) => setUserInput(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && handleInputSubmit()}
                    sx={{ mb: 2 }}
                    placeholder="Введите ваш ответ здесь..."
                  />
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={handleInputSubmit}
                  >
                    Отправить
                  </Button>
                </>
              )}
            </Paper>
          </Box>
        );

      case 'end':
        return (
          <Box
            sx={{
              mt: 2,
              display: 'flex',
              justifyContent: 'center',
              flexDirection: 'column',
              alignItems: 'center',
            }}
          >
            {total > 0 && chosen < total ? (
              <>
                <Typography
                  variant="body2"
                  color="text.primary"
                  sx={{ mb: 2, textAlign: 'center' }}
                >
                  Вы исследовали {chosen} из {total} возможных вариантов в этом
                  уроке.
                </Typography>
                <Button
                  variant="contained"
                  color="primary"
                  startIcon={<ReplayIcon />}
                  onClick={restartGame}
                >
                  Изучить другие варианты
                </Button>
              </>
            ) : (
              <Button
                variant="contained"
                color="secondary"
                startIcon={<ReplayIcon />}
                onClick={restartGame}
              >
                Начать заново
              </Button>
            )}
          </Box>
        );

      default:
        return null;
    }
  };

  if (error) {
    return (
      <Alert severity="error" sx={{ mb: 2 }}>
        {error}
      </Alert>
    );
  }

  if (!processedOptions) {
    return (
      <Alert severity="info" sx={{ mb: 2 }}>
        Загрузка игры...
      </Alert>
    );
  }

  const { character } = processedOptions;
  const { chosen, total } = getTotalChoicesStats();

  return (
    <Box sx={{ mt: 2, p: 2 }} key={`role-play-game-${componentId}`}>
      {/* Информация о персонаже и маскоте */}
      <Paper elevation={3} sx={{ p: 2, mb: 3, bgcolor: 'background.paper' }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item>
            <Avatar
              src={character.avatar}
              alt={character.name}
              sx={{ width: 60, height: 60 }}
            >
              <PersonIcon />
            </Avatar>
          </Grid>
          <Grid item xs>
            <Typography variant="h5" color="text.primary">
              {character.name}
            </Typography>
            <Typography variant="subtitle1" color="text.secondary">
              {character.role}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {character.description}
            </Typography>
          </Grid>

          {/* Счетчик прогресса выборов */}
          {total > 0 && (
            <Grid item>
              <Chip
                label={`Исследовано: ${chosen}/${total} вариантов`}
                color="primary"
                variant="outlined"
              />
            </Grid>
          )}

          {/* Маскот-кот */}
          <Grid item>
            <Tooltip title={getMascotDescription()}>
              <Paper
                elevation={2}
                sx={{
                  p: 1.5,
                  borderRadius: '50%',
                  bgcolor: getMascotBgColor(),
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  cursor: 'pointer',
                }}
              >
                <Avatar
                  src={mascotImage}
                  alt="Кот-помощник"
                  sx={{ width: 60, height: 60 }}
                />
              </Paper>
            </Tooltip>
          </Grid>
        </Grid>
      </Paper>

      {/* История диалогов */}
      <Box sx={{ mb: 3, maxHeight: 400, overflow: 'auto' }}>
        {dialogHistory.map((dialog, index) => (
          <Box
            key={index}
            sx={{
              display: 'flex',
              justifyContent:
                dialog.type === 'user' ? 'flex-end' : 'flex-start',
              mb: 2,
            }}
          >
            <Card
              sx={{
                maxWidth: '80%',
                bgcolor:
                  dialog.type === 'user'
                    ? theme.palette.info.light + '33'
                    : dialog.type === 'feedback'
                    ? dialog.status === 'correct'
                      ? theme.palette.success.light + '33'
                      : theme.palette.error.light + '33'
                    : dialog.type === 'system'
                    ? theme.palette.warning.light + '33'
                    : theme.palette.background.paper,
                color: 'text.primary',
              }}
            >
              {dialog.type === 'character' && (
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    mb: 1,
                    // bgcolor: theme.palette.background.paper,
                    color: 'text.primary',
                  }}
                >
                  <Avatar
                    src={character.avatar}
                    alt={character.name}
                    sx={{ width: 24, height: 24, mr: 1 }}
                  >
                    <PersonIcon fontSize="small" />
                  </Avatar>
                  <Typography variant="subtitle2">{character.name}</Typography>
                </Box>
              )}
              <Typography
                variant="body1"
                color="text.primary"
                dangerouslySetInnerHTML={{
                  __html: DOMPurify.sanitize(dialog.text),
                }}
              />
              {dialog.type === 'feedback' && (
                <Chip
                  color={dialog.status === 'correct' ? 'success' : 'error'}
                  label={
                    dialog.status === 'correct' ? 'Правильно' : 'Неправильно'
                  }
                  size="small"
                  sx={{ mt: 1 }}
                />
              )}
            </Card>
          </Box>
        ))}
      </Box>

      {/* Завершение игры */}
      {gameCompleted ? (
        <Alert severity="success" sx={{ mb: 2 }}>
          <Typography variant="body1" gutterBottom>
            Поздравляем! Вы успешно завершили этот сценарий!
          </Typography>

          {total > 0 && chosen < total && (
            <Box>
              <Typography variant="body2" sx={{ mt: 1, mb: 1 }}>
                Вы исследовали {chosen} из {total} возможных вариантов. Хотите
                изучить другие пути?
              </Typography>
              <Button
                variant="outlined"
                color="primary"
                startIcon={<ReplayIcon />}
                onClick={restartGame}
                sx={{ mt: 1 }}
              >
                Изучить другие варианты
              </Button>
            </Box>
          )}
        </Alert>
      ) : null}

      {/* Текущая сцена */}
      {!gameCompleted && renderCurrentScene()}

      {/* Подсказка о маскоте при первом запуске */}
      {chosen === 0 && total > 0 && (
        <Alert severity="info" sx={{ mb: 2 }}>
          <Typography variant="body2">
            Знакомьтесь, ваш кот-помощник! Исследуйте различные варианты в
            уроке, чтобы улучшить его внешний вид и настроение 😺
          </Typography>
        </Alert>
      )}

      {/* Информация о маскоте при завершении игры */}
      {gameCompleted && (
        <Box sx={{ mt: 2, mb: 2, textAlign: 'center' }}>
          <Paper elevation={3} sx={{ p: 2, bgcolor: 'background.paper' }}>
            <Typography variant="h6" color="text.primary" gutterBottom>
              Прогресс вашего кота-помощника
            </Typography>

            <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
              <Paper
                elevation={2}
                sx={{
                  p: 2,
                  borderRadius: '50%',
                  bgcolor: getMascotBgColor(),
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                }}
              >
                <Avatar
                  src={mascotImage}
                  alt="Кот-помощник"
                  sx={{ width: 120, height: 120 }}
                />
              </Paper>
            </Box>

            <Typography variant="body2" color="text.secondary">
              {getMascotDescription()}
            </Typography>

            {chosen < total && (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                Исследуйте все варианты, чтобы полностью улучшить
                кота-помощника!
              </Typography>
            )}

            {chosen === total && (
              <Typography
                variant="body2"
                color="success.main"
                sx={{ mt: 1, fontWeight: 'bold' }}
              >
                Поздравляем! Вы полностью улучшили кота-помощника, исследовав
                все возможные варианты!
              </Typography>
            )}
          </Paper>
        </Box>
      )}
    </Box>
  );
};

export default RolePlayGame;
